import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Send,
  CheckCircle,
  AlertCircle,
  User,
  Mail,
  RefreshCw,
  Bug,
  Lightbulb,
  ThumbsUp,
  HelpCircle,
  MoreHorizontal,
  MessageSquare
} from 'lucide-react';
import { useTranslation } from '@parentpath/i18n';
import { useAuth } from '@/features/auth';
import { useFeedbackForm, useFeedbackCategories } from '../hooks/useFeedbackForm';
import { useUserProfile, generateMathCaptcha } from '../hooks/useContactForm';
import type { RequestType } from '../types';

const REQUEST_TYPE_ICONS: Record<RequestType, React.ReactNode> = {
  bug: <Bug className="w-4 h-4" />,
  suggestion: <Lightbulb className="w-4 h-4" />,
  improvement: <MessageSquare className="w-4 h-4" />,
  praise: <ThumbsUp className="w-4 h-4" />,
  question: <HelpCircle className="w-4 h-4" />,
  other: <MoreHorizontal className="w-4 h-4" />,
};

const REQUEST_TYPE_VALUES: RequestType[] = ['bug', 'suggestion', 'improvement', 'praise', 'question', 'other'];

export function FeedbackPage() {
  const { user, isLoading: authLoading } = useAuth();
  const { data: profile, isLoading: profileLoading } = useUserProfile();
  const { submit, isSubmitting, isSuccess, error, reset } = useFeedbackForm();
  const { data: feedbackCategories } = useFeedbackCategories();
  const { t, i18n } = useTranslation('help');

  const isLoadingData = authLoading || profileLoading;
  const captchaLocale = (i18n.language === 'en' ? 'en' : 'de') as 'de' | 'en';

  const [feedbackCategoryId, setFeedbackCategoryId] = useState<string>('');
  const [requestType, setRequestType] = useState<RequestType>('suggestion');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [captcha, setCaptcha] = useState(() => generateMathCaptcha(captchaLocale));
  const [captchaAnswer, setCaptchaAnswer] = useState('');
  const [captchaError, setCaptchaError] = useState(false);

  const refreshCaptcha = () => {
    setCaptcha(generateMathCaptcha(captchaLocale));
    setCaptchaAnswer('');
    setCaptchaError(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate captcha
    if (parseInt(captchaAnswer, 10) !== captcha.answer) {
      setCaptchaError(true);
      return;
    }

    try {
      await submit({
        feedback_category_id: feedbackCategoryId || null,
        request_type: requestType,
        subject: subject.trim() || undefined,
        message: message.trim(),
        first_name: profile?.first_name || '',
        last_name: profile?.last_name || '',
      });
    } catch (err) {
      // Error handled by hook
    }
  };

  const handleReset = () => {
    reset();
    setFeedbackCategoryId('');
    setRequestType('suggestion');
    setSubject('');
    setMessage('');
    refreshCaptcha();
  };

  const getMessagePlaceholder = () => {
    if (requestType === 'bug') return t('feedback.messagePlaceholderBug');
    if (requestType === 'suggestion') return t('feedback.messagePlaceholderSuggestion');
    return t('feedback.messagePlaceholderDefault');
  };

  // Success State
  if (isSuccess) {
    return (
      <>
        <div className="glass-effect rounded-[15px] p-6">
          <div className="text-center py-6">
            <div className="w-16 h-16 bg-pp-teal/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-pp-teal" />
            </div>
            <h2 className="text-xl font-semibold text-white mb-2">
              {t('feedback.successTitle')}
            </h2>
            <p className="text-white/70 mb-6">
              {t('feedback.successDescription')}
            </p>
            <div className="flex flex-col gap-3">
              <button
                onClick={handleReset}
                className="w-full py-3 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90 transition-colors"
              >
                {t('feedback.sendAnother')}
              </button>
              <Link
                to="/help"
                className="w-full py-3 glass-effect border border-white/25 text-white font-medium rounded-[15px] hover:bg-white/10 transition-colors text-center"
              >
                {t('contact.backToHelp')}
              </Link>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="mb-6">
        <h1 className="text-xl font-bold text-white">{t('feedback.title')}</h1>
        <p className="text-white/60 text-sm">
          {t('feedback.subtitle')}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-5">
        {/* User Info (read-only) */}
        <div className="glass-effect rounded-[15px] p-4 space-y-3">
          <h3 className="text-sm font-medium text-white/70 mb-3">
            {t('contact.contactDetails')}
          </h3>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-[10px] flex items-center justify-center">
              <User className="h-5 w-5 text-white/60" />
            </div>
            <div className="flex-1">
              <label className="text-xs text-white/50">{t('contact.name')}</label>
              <p className="text-white font-medium">
                {isLoadingData ? (
                  <span className="bg-white/20 rounded h-5 w-32 inline-block animate-pulse" />
                ) : (
                  `${profile?.first_name || ''} ${profile?.last_name || ''}`.trim() || t('contact.notSpecified')
                )}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/10 rounded-[10px] flex items-center justify-center">
              <Mail className="h-5 w-5 text-white/60" />
            </div>
            <div className="flex-1">
              <label className="text-xs text-white/50">{t('contact.email')}</label>
              <p className="text-white font-medium">
                {isLoadingData ? (
                  <span className="bg-white/20 rounded h-5 w-48 inline-block animate-pulse" />
                ) : (
                  user?.email || t('contact.notSpecified')
                )}
              </p>
            </div>
          </div>
        </div>

        {/* Feedback Type Selection */}
        <div>
          <label className="block text-sm font-medium text-white/80 mb-2">
            {t('feedback.typeLabel')}
          </label>
          <div className="grid grid-cols-2 gap-2">
            {REQUEST_TYPE_VALUES.map((value) => (
              <button
                key={value}
                type="button"
                onClick={() => setRequestType(value)}
                className={`flex items-center gap-2 px-3 py-2.5 rounded-[15px] border-2 transition-all text-left
                  ${requestType === value
                    ? 'border-pp-orange bg-pp-orange/20 text-white'
                    : 'border-white/20 bg-white/5 text-white/70 hover:border-white/30'
                  }`}
              >
                <span className={requestType === value ? 'text-pp-orange' : 'text-white/50'}>
                  {REQUEST_TYPE_ICONS[value]}
                </span>
                <span className="text-sm font-medium">{t(`feedback.types.${value}`)}</span>
              </button>
            ))}
          </div>
        </div>

        {/* Category Selection */}
        <div>
          <label className="block text-sm font-medium text-white/80 mb-2">
            {t('feedback.categoryLabel')}
          </label>
          <select
            value={feedbackCategoryId}
            onChange={(e) => setFeedbackCategoryId(e.target.value)}
            className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
          >
            <option value="" className="bg-pp-dark text-white">{t('contact.selectPlaceholder')}</option>
            {feedbackCategories?.map((cat) => (
              <option key={cat.id} value={cat.id} className="bg-pp-dark text-white">
                {cat.name}
              </option>
            ))}
          </select>
        </div>

        {/* Subject (optional) */}
        <div>
          <label className="block text-sm font-medium text-white/80 mb-2">
            {t('feedback.subject')} <span className="text-white/50 font-normal">{t('feedback.optional')}</span>
          </label>
          <input
            type="text"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            placeholder={t('feedback.subjectPlaceholder')}
            maxLength={100}
            className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent"
          />
        </div>

        {/* Message */}
        <div>
          <label className="block text-sm font-medium text-white/80 mb-2">
            {t('contact.message')}
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            required
            minLength={10}
            rows={6}
            placeholder={getMessagePlaceholder()}
            className="w-full px-4 py-3 rounded-[10px] bg-white/10 border border-white/20 text-white placeholder-white/40 focus:outline-none focus:ring-2 focus:ring-pp-orange focus:border-transparent resize-none"
          />
          <p className="text-xs text-white/50 mt-1">
            {t('contact.minChars')}
          </p>
        </div>

        {/* Math Captcha */}
        <div>
          <label className="block text-sm font-medium text-white/80 mb-2">
            {t('contact.captchaLabel')}
          </label>
          <div className="flex items-center gap-3">
            <div className="flex-1 bg-white/10 rounded-[10px] px-4 py-3 flex items-center justify-between border border-white/20">
              <span className="font-medium text-white">
                {captcha.question}
              </span>
              <button
                type="button"
                onClick={refreshCaptcha}
                className="p-1 hover:bg-white/10 rounded-[5px] transition-colors"
                title={t('contact.newCaptcha')}
              >
                <RefreshCw className="h-4 w-4 text-white/60" />
              </button>
            </div>
            <input
              type="number"
              value={captchaAnswer}
              onChange={(e) => {
                setCaptchaAnswer(e.target.value);
                setCaptchaError(false);
              }}
              required
              placeholder="?"
              className={`w-20 px-4 py-3 rounded-[10px] border text-center text-white bg-white/10
                         focus:outline-none focus:ring-2 focus:border-transparent
                         ${captchaError
                           ? 'border-red-500 focus:ring-red-500'
                           : 'border-white/20 focus:ring-pp-orange'
                         }`}
            />
          </div>
          {captchaError && (
            <p className="text-sm text-red-400 mt-1">
              {t('contact.captchaError')}
            </p>
          )}
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-[15px] p-4 flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-400 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-medium">{t('errors.sendFailed')}</p>
              <p className="text-red-300 text-sm">
                {error.message || t('errors.sendFailedFallback')}
              </p>
            </div>
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={isSubmitting || !message.trim() || message.length < 10}
          className="w-full py-4 bg-pp-orange text-white font-medium rounded-[15px] hover:bg-pp-orange/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
        >
          {isSubmitting ? (
            <>
              <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
              {t('contact.sending')}
            </>
          ) : (
            <>
              <Send className="h-5 w-5" />
              {t('feedback.send')}
            </>
          )}
        </button>

        <p className="text-xs text-white/50 text-center">
          {t('contact.privacyPrefix')}{' '}
          <Link to="/legal/privacy" className="text-pp-orange hover:underline">
            {t('contact.privacyLink')}
          </Link>{' '}
          {t('contact.privacySuffix')}
        </p>
      </form>
    </>
  );
}
